/**
 * Spectral analysis for the voice check.
 *
 * The question this answers: across every sound you can make, is there a
 * frequency region you can't put energy into?
 *
 * WHY SEVERAL VOWELS. A single vowel has deep spectral valleys purely from
 * phonetics — /i/ has a high second formant around 2300 Hz, /a/ has low
 * formants around 700 and 1200 Hz. A dip in one vowel is the shape of your
 * mouth, not a property of your voice. So we take the *loudest* level each band
 * ever reached across all the vowels: "the best you managed anywhere". A region
 * that stays weak even at its best is the interesting one.
 *
 * WHY THE MICROPHONE MOSTLY CANCELS. Every recording goes through the same
 * microphone, so its response is baked equally into all of them. It is also
 * smooth — microphones roll off gently, they don't carve narrow notches. The
 * detrend below subtracts a local baseline, which removes anything smooth
 * (mic response, the natural downward tilt of a voice) and leaves narrow dips.
 * That is what makes this more defensible than comparing one recording against
 * an absolute reference, where ~93% of the spectral shape is the phone.
 */

/* ------------------------------------------------------------------ *
 * FFT — iterative radix-2, in place
 * ------------------------------------------------------------------ */

/**
 * In-place complex FFT. `re` and `im` must be the same power-of-two length.
 *
 * Written out rather than pulled in: a long-term average spectrum runs this
 * over hundreds of frames, and the naive DFT used elsewhere in this codebase is
 * O(n²) — at n=2048 that is ~4.2 million operations per frame instead of
 * ~22 thousand.
 */
export function fft(re: Float32Array, im: Float32Array): void {
  const n = re.length;
  if (n <= 1) return;
  if ((n & (n - 1)) !== 0) throw new Error('FFT length must be a power of two');

  // Bit-reversal permutation
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j], re[i]];
      [im[i], im[j]] = [im[j], im[i]];
    }
  }

  // Danielson–Lanczos
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wRe = Math.cos(ang);
    const wIm = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let curRe = 1;
      let curIm = 0;
      for (let k = 0; k < len / 2; k++) {
        const aRe = re[i + k];
        const aIm = im[i + k];
        const bRe = re[i + k + len / 2] * curRe - im[i + k + len / 2] * curIm;
        const bIm = re[i + k + len / 2] * curIm + im[i + k + len / 2] * curRe;
        re[i + k] = aRe + bRe;
        im[i + k] = aIm + bIm;
        re[i + k + len / 2] = aRe - bRe;
        im[i + k + len / 2] = aIm - bIm;
        const nextRe = curRe * wRe - curIm * wIm;
        curIm = curRe * wIm + curIm * wRe;
        curRe = nextRe;
      }
    }
  }
}

/* ------------------------------------------------------------------ *
 * Bands
 * ------------------------------------------------------------------ */

/**
 * ISO 266 preferred 1/3-octave centre frequencies covering the voice range.
 *
 * Below ~100 Hz there is little usable voice energy on a phone microphone;
 * above ~8 kHz there is little that matters here and phone mics roll off hard.
 */
export const BAND_CENTERS = [
  100, 125, 160, 200, 250, 315, 400, 500, 630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000,
  6300, 8000,
] as const;

const BAND_RATIO = Math.pow(2, 1 / 6); // half a third-octave either side

export function bandEdges(center: number): { lo: number; hi: number } {
  return { lo: center / BAND_RATIO, hi: center * BAND_RATIO };
}

/* ------------------------------------------------------------------ *
 * Long-term average spectrum
 * ------------------------------------------------------------------ */

const FFT_N = 2048;

function hann(n: number): Float32Array {
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
  return w;
}

/**
 * Long-term average spectrum, in dB per 1/3-octave band.
 *
 * Welch's method: overlapping Hann-windowed frames, power averaged across all
 * of them, then summed into bands. Frames quieter than the loudest by more than
 * `floorDb` are skipped so silence and breaths don't drag the average down.
 */
export function ltasBands(
  signal: Float32Array,
  sampleRate: number,
  floorDb = -30,
): Float32Array {
  const window = hann(FFT_N);
  const hop = FFT_N / 2;
  const bandPower = new Float64Array(BAND_CENTERS.length);
  const binHz = sampleRate / FFT_N;

  // First pass: frame levels, so the floor can be relative.
  const levels: number[] = [];
  for (let off = 0; off + FFT_N <= signal.length; off += hop) {
    let sum = 0;
    for (let i = 0; i < FFT_N; i++) sum += signal[off + i] * signal[off + i];
    levels.push(Math.sqrt(sum / FFT_N));
  }
  if (levels.length === 0) return new Float32Array(BAND_CENTERS.length).fill(-120);

  const peak = Math.max(...levels);
  const cutoff = peak * Math.pow(10, floorDb / 20);

  let used = 0;
  let frameIdx = 0;
  const re = new Float32Array(FFT_N);
  const im = new Float32Array(FFT_N);

  for (let off = 0; off + FFT_N <= signal.length; off += hop, frameIdx++) {
    if (levels[frameIdx] < cutoff) continue;
    used++;

    for (let i = 0; i < FFT_N; i++) {
      re[i] = signal[off + i] * window[i];
      im[i] = 0;
    }
    fft(re, im);

    // Accumulate power into bands. Only the first half is unique.
    for (let k = 1; k < FFT_N / 2; k++) {
      const hz = k * binHz;
      const power = re[k] * re[k] + im[k] * im[k];
      for (let b = 0; b < BAND_CENTERS.length; b++) {
        const { lo, hi } = bandEdges(BAND_CENTERS[b]);
        if (hz >= lo && hz < hi) {
          bandPower[b] += power;
          break;
        }
      }
    }
  }

  const out = new Float32Array(BAND_CENTERS.length);
  for (let b = 0; b < BAND_CENTERS.length; b++) {
    const mean = used > 0 ? bandPower[b] / used : 0;
    out[b] = 10 * Math.log10(Math.max(mean, 1e-12));
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Combining vowels and finding dips
 * ------------------------------------------------------------------ */

/**
 * Remove each recording's overall loudness, keeping only its shape.
 *
 * Without this, combining vowels is a loudness contest rather than a
 * comparison. Titze (1992) measured maximum SPL for /a/ exceeding /i/ by more
 * than 10 dB, so /a/ simply wins every band and the other four vowels
 * contribute nothing — which defeats the whole reason for recording five.
 */
export function normaliseLevel(spectrum: Float32Array): Float32Array {
  let sum = 0;
  for (let i = 0; i < spectrum.length; i++) sum += spectrum[i];
  const mean = sum / spectrum.length;
  const out = new Float32Array(spectrum.length);
  for (let i = 0; i < spectrum.length; i++) out[i] = spectrum[i] - mean;
  return out;
}

/**
 * Combine several vowels into one "what could you manage here" curve.
 *
 * Each spectrum is level-normalised first (see above), then a high percentile
 * is taken per band rather than a raw maximum. A raw max is biased upward by
 * however many recordings you happen to have — with more takes it drifts higher
 * for no real reason, an effect documented in voice-range-profile retest work.
 * The 80th percentile keeps the "best you managed" character while discarding
 * a single freak take.
 *
 * The underlying operation has a published relative: Praat's
 * `Ltas: Get local peak height` measures a peak against its flanking regions.
 * Negate that and you have the dip depth this module reports.
 */
export function combineAcross(spectra: Float32Array[], percentile = 0.8): Float32Array {
  const out = new Float32Array(BAND_CENTERS.length).fill(-120);
  if (spectra.length === 0) return out;

  const levelled = spectra.map(normaliseLevel);

  for (let b = 0; b < out.length; b++) {
    const column = levelled.map((s) => s[b]).sort((x, y) => x - y);
    const pos = percentile * (column.length - 1);
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    out[b] = column[lo] + (column[hi] - column[lo]) * (pos - lo);
  }
  return out;
}

/**
 * Subtract a local baseline so only narrow dips survive.
 *
 * The baseline is a moving median across ±`half` bands. A median rather than a
 * mean so one deep notch doesn't drag its own baseline down with it. Anything
 * smooth — the microphone's response, the natural roll-off of a voice — is in
 * the baseline and cancels; a narrow notch does not.
 */
export function detrend(bands: Float32Array, half = 3): Float32Array {
  const out = new Float32Array(bands.length);
  for (let i = 0; i < bands.length; i++) {
    const lo = Math.max(0, i - half);
    const hi = Math.min(bands.length - 1, i + half);
    const window: number[] = [];
    for (let j = lo; j <= hi; j++) window.push(bands[j]);
    window.sort((a, b) => a - b);
    const mid = window.length >> 1;
    const median =
      window.length % 2 ? window[mid] : (window[mid - 1] + window[mid]) / 2;
    out[i] = bands[i] - median;
  }
  return out;
}

export type MissingBand = {
  /** 1/3-octave centre, Hz. */
  centerHz: number;
  /** How far below its neighbours this band sits, dB. Positive = deeper. */
  depthDb: number;
  /** Octave-folded into the device's range, Hz. */
  gateHz: number;
  /** How many halvings were applied. */
  octavesDown: number;
};

/**
 * Lowest band worth analysing, as a multiple of the speaker's pitch.
 *
 * A voice is a set of harmonics spaced f0 apart. A 1/3-octave band centred at
 * c is about 0.23·c wide, so it only reliably contains a harmonic once
 * 0.23·c ≥ f0 — that is, above roughly 4.3·f0. Below that, bands fall *between*
 * harmonics and read as deep gaps that aren't there.
 *
 * Measured directly: a clean 120 Hz voice with no notch at all shows −26 dB at
 * the 160 Hz band and −32 dB at 200 Hz, purely because its harmonics sit at 120
 * and 240 and nothing lands in between. Above ~5·f0 the same voice is flat to
 * within 0.1 dB. Hence 5, with a little margin over the theoretical 4.3.
 */
export const MIN_BAND_MULTIPLE_OF_F0 = 5;

/**
 * Lowest band worth reporting at all, regardless of pitch.
 *
 * Phone microphones high-pass aggressively and attenuate around the
 * fundamental, so levels below roughly 160 Hz say more about the handset than
 * the speaker. Awan et al. (2022) find spectral measures usable for same-device
 * comparison but not as absolute values, and this is where that breaks down
 * first.
 */
export const MIN_RELIABLE_HZ = 160;

/**
 * Halve until the value lands inside the device's gate range.
 *
 * Note this is a convention, not a physical equivalence — a magnetic field at
 * 150 Hz is not "the same note" as 2400 Hz to tissue. It is how the result is
 * brought into a range the coil can actually produce.
 */
export function foldToGateRange(hz: number, lo = 0.5, hi = 200): { gateHz: number; octaves: number } {
  let f = hz;
  let n = 0;
  while (f > hi) {
    f /= 2;
    n++;
  }
  while (f < lo) {
    f *= 2;
    n--;
  }
  return { gateHz: Number(f.toFixed(2)), octaves: n };
}

/** Which bands can be judged at all, given this speaker's pitch. */
export function analysableRange(f0: number, half = 3): { first: number; last: number } {
  // Bands at the very ends have a truncated median window, so their baseline
  // isn't comparable to the rest.
  let first = half;
  const last = BAND_CENTERS.length - 1 - half;

  // Two independent floors: harmonic density (pitch-dependent) and microphone
  // reliability (fixed). Whichever bites harder wins.
  const floor = Math.max(MIN_RELIABLE_HZ, f0 > 0 ? f0 * MIN_BAND_MULTIPLE_OF_F0 : 0);
  while (first <= last && BAND_CENTERS[first] < floor) first++;

  return { first, last };
}

/**
 * Rank bands from most to least missing.
 *
 * `f0` is the speaker's pitch — needed because bands below ~5·f0 cannot be
 * judged (see MIN_BAND_MULTIPLE_OF_F0). Pass 0 to skip that check.
 *
 * `minDepthDb` keeps ordinary ripple out — every spectrum wobbles a decibel or
 * two, and calling that "missing" would fill the list with noise.
 */
export function findMissing(best: Float32Array, f0: number, minDepthDb = 3): MissingBand[] {
  const residual = detrend(best);
  const { first, last } = analysableRange(f0);
  const out: MissingBand[] = [];

  for (let b = first; b <= last; b++) {
    const depth = -residual[b];
    if (depth < minDepthDb) continue;
    const center = BAND_CENTERS[b];
    const { gateHz, octaves } = foldToGateRange(center);
    out.push({ centerHz: center, depthDb: Number(depth.toFixed(1)), gateHz, octavesDown: octaves });
  }

  return out.sort((a, b) => b.depthDb - a.depthDb);
}
